<template>
    <v-row 
        v-if="showActionMetadata"
        class="ma-0"
    >
        <v-col cols="12" class="px-0">
            <v-text-field
                :model-value="resolveActionBy"
                readonly
                :label="`${resolveLabelForAction} por:`"
                :disabled="props?.disabled || isSubmitting"
                autocomplete="off"
            ></v-text-field>
        </v-col>
    </v-row>
    <v-row 
        v-if="showActionMetadata"
        class="ma-0"
    >
        <v-col cols="12" class="px-0">
            <v-text-field
                :model-value="resolveActionAt"
                readonly
                :label="`${resolveLabelForAction} a:`"
                :disabled="props?.disabled || isSubmitting"
                autocomplete="off"
            ></v-text-field>
        </v-col>
    </v-row>
    <v-row 
        v-if="['reject', 'deny', 'delete', 'cancel'].includes(props?.action)"
        class="ma-0"
    >
        <v-col cols="12" class="px-0">
            <v-textarea
                v-model="reasonValue"
                label="Razão"
                :readonly="props?.asHistory || (props?.readonly && !isReasonAction)"
                :clearable="!props?.asHistory && isReasonAction"
                :disabled="props?.disabled || isSubmitting"
                :rules="
                    ['reject', 'deny', 'delete', 'cancel'].includes(props?.action)
                    ? [formValidationRules.required(resolveReasonForAction)]
                    : []
                "
            >

            </v-textarea>
        </v-col>
    </v-row>
</template>

<script setup>
import { computed, ref } from 'vue';
import formValidationRules from '../../../composables/formValidationRules';
import { normalizeDateTime } from '../../../composables/datetime';

const props = defineProps({
    disabled : {
        type: Boolean,
        default: false,
    },
    readonly : {
        type: Boolean,
        default: false,
    },
    action: {
        type: String,
        default: null,
    },
    statusInformation : {
        type: Object,
        default: null
    },
    at : {
        type: String,
        default: null,
    },
    by: {
        type: [String, Object],
        default: null,
    },
    reason : {
        type: String,
        default: null,
    },
    isSubmitting: {
        type: Boolean,
        default: false,
    },
    asHistory : {
        type: Boolean,
        default:false,
    }
})

const emit = defineEmits([
    "update:reason",
]);

const isReasonAction = computed(() => {
    return ["reject", "deny", "delete", "cancel"].includes(props.action);
});

const showModificationUserInput = computed(() => {
    return [
        "create",
        "approve",
        "cancel",
        "delete",
        "deny",
        "reject",
        "process",
    ].includes(props?.action)
});

const showActionMetadata = computed(() => {
    return showModificationUserInput.value
        && (
            props?.asHistory
            || props?.action !== "cancel"
        );
})

const resolveLabelForAction = computed(() => {

    if(!props?.action) return;
    
    let label;

    if(!props?.asHistory) {

        switch (props?.action) {
            case "approve":
            case "cancel":
            case "deny":
            case "reject":
            case "delete":
                label = "Criado"
                break;
    
            case "process":
                label = "Aprovado";
                break;
            
            default:
    
                throw new TypeError(
                    `Unsupported value for action ('${action}')`,
                    {
                        cause: "ValueError",
                    }
                )
                break;
        }
    }
    else {

        switch (props?.action) {
            case "approve":
                label = "Aprovado";
                break;
            case "cancel":
                label = "Cancelado";
                break;
            case "create":
                label = "Criado";
                break;
            case "deny":
            case "reject":
                label = "Recusado"
                break;
    
            case "process":
                label = "Processado";
                break;
    
            case "delete":
                label = "Apagado";
                break;
            
            default:
    
                throw new TypeError(
                    `Unsupported value for action ('${action}')`,
                    {
                        cause: "ValueError",
                    }
                )
                break;
        }
    }


    return label;
});

const resolvePropertyForAction = computed(() => {

    if(!props?.action) return;

    let property;

    if(!props?.asHistory) {
        
        switch (props?.action) {
            case "approve":
            case "cancel":
            case "deny":
            case "delete":
            case "reject":
                property = "created"
                break;
    
            case "process":
                property = "approved";
                break;
            
            default:
    
                throw new TypeError(
                    `Unsupported value for action ('${action}')`,
                    {
                        cause: "ValueError",
                    }
                )
                break;
        }
    }
    else {
        switch (props?.action) {
            case "approve":
            case "create":
            case "delete":
                property = `${props.action}d`;
                break;

            case "process":
                property = `${props.action}ed`;
                break;

            case "deny":
            case "reject":
                property = "denied";
                break;

            case "cancel":
                property = "cancelled";
                break;
        
            default:
                throw new TypeError(
                    `Unsupported value for action ('${action}')`,
                    {
                        cause: "ValueError",
                    }
                )
                break;
        }
    }

    return property
});

const resolveReasonForAction = computed(() => {

    let reason = null;
    switch (props?.action) {
        case "deny":
        case "reject":
            reason =  props?.statusInformation?.denied_reason;
            break;
        
        case "delete":
            reason = props?.statusInformation?.deleted_reason;
            break;

        case "cancel":
            reason = props?.statusInformation?.cancellation_reason;
            break;

        default:
            break;
    }

    return reason ?? props?.reason ?? null;
});

const reasonValue = computed({
    get: () => resolveReasonForAction.value,
    set: (value) => emit("update:reason", value),
});

const resolveActionBy = computed( () => {
    // despite the fact that the Eloquent relationships are defined as
    // *By() (example: approvedBy() ), laravel's default settings will 
    // rename properties to snakecase (example: approved_by)
    // so we check both options here to be safe
    const userModel = props?.statusInformation?.[
                            `${resolvePropertyForAction.value}_by`
                        ] 
                        ?? props?.statusInformation?.[
                            `${resolvePropertyForAction.value}By`
                        ] 
                        ?? props?.by;

    if (!userModel || typeof userModel === "string") {
        return userModel ?? "";
    }

    return `${userModel.worker_code ?? "Código Indisponível"} - ${userModel.name ?? "Nome Indisponível"}`.trim();
});

const resolveActionAt = computed( () => {

    const actionDateTime = props?.statusInformation?.[
        `${resolvePropertyForAction.value}_at`
    ] ?? props?.at;

    if (!actionDateTime ) {
        return null;
    }

    return normalizeDateTime(actionDateTime);
});

const resolveActionReason = computed(() => {
    const actionReason = props?.statusInformation?.reason
                            ?? props?.reason;

    if(!actionReason && !String(actionReason).trim()) {
        return null;
    }

    return String(actionReason);
}); 

async function validate() {

} 

async function submit() {
    
}



</script>

<style scoped>

</style>